// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

// Custom packages
import { ApiResponse, ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { SupplierInOutFilterOptions } from 'src/app/shared/interfaces/supplier-in-out-filter-options.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class PropertySuppliersInOutService {

  /**
   * Filter-options cache, scoped by the currently requested company.
   * The service is a root singleton, so the cache survives navigation between
   * list views within the same session. A change of X-Requested-Company yields a
   * different cache key and therefore transparently invalidates the cached value.
   */
  private filterOptionsCache: { company: string; options: SupplierInOutFilterOptions } | null = null;

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private cookieService: CookieService
  ) { }

  /**
   * Create a new Tag NFC
   *
   * @since 1.0.0
   *
   * @param data Supplier In Out
   * @returns Observable<any>
   */
  create(data: any): Observable<any> {
    return this.apiService.apiCall('POST', `supplier-in-out`, data);
  }

  /**
   * Get all Suppliers In Out list of property
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getPropertyList(propertyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `supplier-in-out/property/${propertyId}`, params);
  }
  
  /**
   * Get all Suppliers In Out list
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `supplier-in-out`, params);
  }

  /**
   * Get the dropdown options (suppliers and operators) for the supplier-in-out
   * list filters, scoped to the currently requested company.
   *
   * Requires the `canReadSupplierInOutList` permission: callers must handle a
   * 403 by falling back to the legacy free-text filters.
   *
   * The successful payload is cached per company; the call is only re-issued
   * when X-Requested-Company changes. Errors are never cached.
   *
   * @since 3.0.0
   *
   * @returns Observable<SupplierInOutFilterOptions>
   */
  getFilterOptions(): Observable<SupplierInOutFilterOptions> {
    const requestedCompany = this.cookieService.get('requestedCompany') || '';

    if (this.filterOptionsCache && this.filterOptionsCache.company === requestedCompany) {
      return of(this.filterOptionsCache.options);
    }

    return this.apiService.apiCall('GET', `supplier-in-out/filter-options`)
      .pipe(
        map((res: ApiResponse<SupplierInOutFilterOptions>) => {
          const payload = (res && res.data) ? res.data : { suppliers: [], operators: [] };
          return {
            suppliers: Array.isArray(payload.suppliers) ? payload.suppliers : [],
            operators: Array.isArray(payload.operators) ? payload.operators : [],
          };
        }),
        tap((options: SupplierInOutFilterOptions) => {
          this.filterOptionsCache = { company: requestedCompany, options };
        })
      );
  }

  /**
   * Get an exportation file of all Tag NFC of property
   *
   * @since 1.0.0
   */
  getExportProperty(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `supplier-in-out/property/export/${propertyId}`, {  }, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  /**
   * Get an exportation file of all Tag NFC of property
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService.apiCall('GET', `supplier-in-out/export`, {  }, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  
  /**
   * Update fields with given tagId
   *
   * @since 1.0.0
   *
   * @param tagId ID of property to update
   */
  updateFields(tagId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PATCH', `supplier-in-out/${tagId}`, data);
  }

  delete(tagId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `supplier-in-out/${tagId}`);
  }
}
